import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { defaultPageNo } from "utils/constants";
import { useSnackbar } from "hooks/useSnackbar";

export type BulkAction = "archive" | "restore" | "enable" | "disable" | "delete";

/** Which rows a live-view selection is made of. A selection is always all one state. */
export type SelectionMode = "enabled" | "disabled";

export interface ContentArchiveResult {
  action: BulkAction;
  successCount: number;
  successIds: string[];
  skippedIds: string[];
  failedIds: string[];
  /** One finished sentence per skipped id. Only the bulk status and bulk delete routes send these. */
  skipReasons: { id: string; reason: string }[];
  /** Ids left unprocessed because a later chunk's call failed; earlier chunks are already applied. */
  unsentIds: string[];
  /** The error of the call that failed, set together with `unsentIds`. */
  unsentReason?: string;
}

/** The server rejects a whole call carrying more ids than this, so larger selections go in chunks. */
const MAX_IDS_PER_CALL = 1000;

interface ContentArchiveOptions {
  /** Used in every user-facing string, e.g. { singular: "pack", plural: "packs" }. */
  label: { singular: string; plural: string };
  /** True when the record is live in-game, i.e. enabled. */
  isLive?: (row: any) => boolean;
  archive: (ids: string[]) => Promise<any>;
  unarchive: (ids: string[]) => Promise<any>;
  /**
   * Bulk enable/disable route. Supplying it turns on selection modes: the selection is either all
   * enabled rows (Disable) or all disabled rows (Enable, Archive, Delete), picked from the header
   * dropdown or by the first row ticked. Without it the screen keeps the archive-only selection,
   * where enabled rows cannot be ticked at all.
   */
  bulkStatus?: (ids: string[], enabled: boolean) => Promise<any>;
  /** Bulk delete route. Leave it out for roles that may not delete, and Delete is not offered. */
  bulkDelete?: (ids: string[]) => Promise<any>;
  /** The rows on the current page, which "select all" picks from. Needed with `bulkStatus`. */
  rows?: any[];
  getRowId?: (row: any) => string;
  /** Refresh the list once the action has finished. */
  onCompleted: () => void | Promise<void>;
  /** Reset pagination when the view switches between live and archived. */
  onViewChange?: () => void;
}

/**
 * Sends the ids in chunks the server accepts and merges the per-id outcomes into one result.
 * A failed first call is rethrown, as nothing has been applied yet. A failed later call stops
 * there and returns what the earlier chunks did, with the rest in `unsentIds`.
 */
const runInChunks = async (
  action: BulkAction,
  ids: string[],
  call: (ids: string[]) => Promise<any>,
): Promise<ContentArchiveResult> => {
  const merged: ContentArchiveResult = {
    action,
    successCount: 0,
    successIds: [],
    skippedIds: [],
    failedIds: [],
    skipReasons: [],
    unsentIds: [],
  };

  for (let start = 0; start < ids.length; start += MAX_IDS_PER_CALL) {
    let response: any;
    try {
      response = await call(ids.slice(start, start + MAX_IDS_PER_CALL));
    } catch (error: any) {
      if (start === 0) throw error;
      merged.unsentIds = ids.slice(start);
      merged.unsentReason = error?.message;
      return merged;
    }
    // The backend reports per-id outcomes; a partial result is a success, not an error.
    const data = response?.data ?? response ?? {};
    merged.successCount += data?.successCount ?? 0;
    merged.successIds.push(...(data?.successIds ?? []));
    merged.skippedIds.push(...(data?.skippedIds ?? []));
    merged.failedIds.push(...(data?.failedIds ?? []));
    merged.skipReasons.push(...(data?.skipReasons ?? []));
  }

  return merged;
};

/**
 * Drives the bulk actions for one list screen: which view is showing, what is selected, and the
 * confirm -> act -> report cycle.
 *
 * The view lives in an `archived` query param rather than component state, so the archived list
 * is a real address - the browser back button, a refresh and "open in new tab" all behave.
 */
const useContentArchive = ({
  label,
  isLive,
  archive,
  unarchive,
  bulkStatus,
  bulkDelete,
  rows,
  getRowId = (row: any) => row?._id,
  onCompleted,
  onViewChange,
}: ContentArchiveOptions) => {
  const { showSnackbar } = useSnackbar();
  const [searchParams, setSearchParams] = useSearchParams();
  const archived = searchParams.get("archived") === "true";

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [mode, setMode] = useState<SelectionMode | null>(null);
  const [pending, setPending] = useState<{
    action: BulkAction;
    ids: string[];
  } | null>(null);
  const [result, setResult] = useState<ContentArchiveResult | null>(null);
  const [busy, setBusy] = useState(false);

  // In the archive every row is disabled, so modes only apply to the live view.
  const usesModes = !archived && !!bulkStatus;

  const stateOf = (row: any): SelectionMode =>
    isLive?.(row) ? "enabled" : "disabled";

  // A selection made in one view means nothing in the other.
  useEffect(() => {
    setSelectedIds([]);
  }, [archived]);

  // An empty selection has no state yet, so the next tick or "select all" may pick either.
  useEffect(() => {
    if (!selectedIds.length) setMode(null);
  }, [selectedIds]);

  /**
   * Switches between the live list and the archive in exactly ONE navigation.
   *
   * react-router's `setSearchParams` is not a state updater: its functional form is handed the
   * search params captured in the calling render, not the latest ones, and each call navigates to
   * a freshly built query string. Two calls in the same handler therefore both build off the same
   * stale snapshot and the last one silently discards the first one's change. `onViewChange` runs
   * first precisely so that its own param write is the one superseded, while its local pagination
   * reset still applies. Replacing rather than pushing then collapses both navigations into the
   * single history entry `onViewChange` already pushed, so one press of the browser's back button
   * leaves the archive.
   */
  const setView = (nextArchived: boolean) => {
    onViewChange?.();
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (nextArchived) next.set("archived", "true");
        else next.delete("archived");
        next.set("pageNo", String(defaultPageNo));
        return next;
      },
      { replace: true },
    );
  };

  const showArchived = () => setView(true);
  const showLive = () => setView(false);

  /** Table selection handler. The first row ticked sets the mode when none has been chosen yet. */
  const changeSelection = (ids: string[]) => {
    if (usesModes && !mode && ids.length) {
      const lastTicked = (rows ?? []).find(
        (row) => getRowId(row) === ids[ids.length - 1],
      );
      if (lastTicked) setMode(stateOf(lastTicked));
    }
    setSelectedIds(ids);
  };

  const pageIdsOf = (state: SelectionMode) =>
    (rows ?? []).filter((row) => stateOf(row) === state).map(getRowId);

  /**
   * Ticks every row of one state on the current page. Picks of the same state made on other pages
   * are kept; switching state starts a fresh selection, because a selection is all one state.
   */
  const selectAll = (nextMode: SelectionMode) => {
    const pageIds = pageIdsOf(nextMode);
    if (!pageIds.length) return;
    const kept = mode === nextMode ? selectedIds : [];
    setMode(nextMode);
    setSelectedIds([...new Set([...kept, ...pageIds])]);
  };

  /** The option for one state: unavailable when this page has none, and says what it replaces. */
  const selectAllOption = (state: SelectionMode) => {
    const available = pageIdsOf(state).length;
    const replaced = mode && mode !== state ? selectedIds.length : 0;
    return {
      label: `Select all ${state} ${label.plural}`,
      disabled: !available,
      hint: !available
        ? "None on this page"
        : replaced
          ? `Replaces the ${replaced} ${mode} ${
              replaced === 1 ? label.singular : label.plural
            } selected now`
          : undefined,
      onSelect: () => selectAll(state),
    };
  };

  /** Replaces the table's header checkbox with a dropdown; null keeps the checkbox. */
  const selectAllOptions = usesModes
    ? [selectAllOption("enabled"), selectAllOption("disabled")]
    : null;

  /** Non-null disables the row's checkbox and explains why. */
  const rowSelectDisabledReason = useMemo(
    () => (row: any) => {
      if (archived) return null;
      if (!bulkStatus) {
        return isLive?.(row)
          ? `An active ${label.singular} cannot be archived. Deactivate it first.`
          : null;
      }
      if (!mode || stateOf(row) === mode) return null;
      return `Only ${mode} ${label.plural} can be selected together. Clear the selection to pick ${
        mode === "enabled" ? "disabled" : "enabled"
      } ones.`;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [archived, bulkStatus, mode, isLive, label.singular, label.plural],
  );

  /** The actions the current selection allows, in the order the action bar shows them. */
  const actions: BulkAction[] = archived
    ? ["restore", ...(bulkDelete ? (["delete"] as const) : [])]
    : !bulkStatus
      ? ["archive"]
      : mode === "enabled"
        ? ["disable"]
        : ["enable", "archive", ...(bulkDelete ? (["delete"] as const) : [])];

  const callFor = (action: BulkAction): ((ids: string[]) => Promise<any>) => {
    switch (action) {
      case "restore":
        return unarchive;
      case "enable":
        return (ids) => bulkStatus(ids, true);
      case "disable":
        return (ids) => bulkStatus(ids, false);
      case "delete":
        return bulkDelete;
      default:
        return archive;
    }
  };

  /**
   * Opens the confirm dialog. Row menus pass their own id and get archive (or restore in the
   * archive); the action bar passes the action and acts on the selection.
   */
  const requestAction = (ids?: string[], action?: BulkAction) => {
    const target = ids?.length ? ids : selectedIds;
    if (!target.length) return;
    setPending({ action: action ?? (archived ? "restore" : "archive"), ids: target });
  };

  const confirmAction = async () => {
    if (!pending) return;
    const { action, ids } = pending;
    setBusy(true);
    try {
      const data = await runInChunks(action, ids, callFor(action));
      setPending(null);
      setResult(data);
      // Ids a failed later chunk never reached stay selected, so a retry acts on exactly those.
      setSelectedIds(data.unsentIds);
      await onCompleted();
    } catch (error: any) {
      // The first call failed (role, validation or server unreachable), so there is nothing to
      // report per item. The selection is kept so the action can be retried.
      setPending(null);
      showSnackbar(error?.message, "error");
      // The call may have reached the server before failing, so show the list as the server has it now.
      await onCompleted();
    } finally {
      setBusy(false);
    }
  };

  return {
    label,
    archived,
    mode,
    selectedIds,
    setSelectedIds: changeSelection,
    clearSelection: () => setSelectedIds([]),
    selectAllOptions,
    actions,
    showArchived,
    showLive,
    rowSelectDisabledReason,
    requestAction,
    pending,
    confirmAction,
    confirmOpen: !!pending,
    // Kept open while the call runs: a large pack enable can take a few seconds.
    closeConfirm: () => {
      if (!busy) setPending(null);
    },
    result,
    closeResult: () => setResult(null),
    busy,
  };
};

export type ContentArchive = ReturnType<typeof useContentArchive>;

export default useContentArchive;
